import { Link } from 'react-router-dom'
import { useNodes, type Node, type NodeMetrics } from '@/lib/cluster'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { GpuRow } from '@/components/GpuRow'
import { StatusBadge } from '@/components/StatusBadge'
import { bytes, relative } from '@/lib/format'

function MetricBar({
  label,
  value,
  sub,
}: {
  label: string
  value: number | null | undefined
  sub?: string
}) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value))
  const display = value == null ? '—' : `${pct.toFixed(1)}%`
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {sub ? <span className="mr-2">{sub}</span> : null}
          {display}
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function pctOf(used: number | null, total: number | null): number | null {
  if (used == null || total == null || total <= 0) return null
  return (used / total) * 100
}

function hbAgeSeconds(ts: string | null | undefined): number | null {
  if (!ts) return null
  return Math.floor((Date.now() - new Date(ts).getTime()) / 1000)
}

function hbColorClass(age: number | null): string {
  // Thresholds match the collector push interval (30 s). A normal heartbeat
  // oscillates between 0 and 30 s, so anything below the interval is fine.
  // Amber = one missed push (> 45 s ≈ 1.5× interval), red = two missed
  // pushes (> 90 s = 3× interval ≈ node_down rule's window).
  if (age == null) return 'text-muted-foreground'
  if (age > 90) return 'text-destructive'
  if (age > 45) return 'text-amber-400'
  return 'text-muted-foreground'
}

function NodeCard({ n }: { n: Node }) {
  const m: NodeMetrics | null = n.latest_metrics
  const cpuPct = m?.cpu_pct ?? null
  const memPct = pctOf(m?.mem_used ?? null, m?.mem_total ?? null)
  // Aggregate disk_used / disk_total used to be inflated 40-50× by
  // bind-mount duplicates inside the collector. The fix dedups upstream
  // and exposes a per-mountpoint list — we render that when available
  // and only fall back to the aggregate for nodes still on old code.
  const hasDisks = (n.disks ?? []).length > 0
  const diskPct = pctOf(m?.disk_used ?? null, m?.disk_total ?? null)
  const hbAge = hbAgeSeconds(m?.ts)
  const hbColor = hbColorClass(hbAge)

  return (
    <Link to={`/nodes/${n.id}`} className="block">
    <Card className="h-full hover:border-primary/50 transition cursor-pointer">
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{n.name}</CardTitle>
        <div className="flex items-center gap-2">
          {hbAge != null && (
            <span className={`text-xs ${hbColor}`}>hb {hbAge}s</span>
          )}
          <StatusBadge status={n.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {m ? (
          <>
            <MetricBar label="CPU" value={cpuPct} />
            <MetricBar
              label="Memory"
              value={memPct}
              sub={`${bytes(m.mem_used)} / ${bytes(m.mem_total)}`}
            />
            {hasDisks ? (
              <div className="space-y-1">
                <div className="text-xs font-medium text-foreground">Disk</div>
                {(n.disks ?? []).map((d) => (
                  <MetricBar
                    key={d.mountpoint}
                    label={d.mountpoint}
                    value={pctOf(d.used_bytes, d.total_bytes)}
                    sub={`${bytes(d.used_bytes)} / ${bytes(d.total_bytes)}`}
                  />
                ))}
              </div>
            ) : (
              <MetricBar
                label="Disk"
                value={diskPct}
                sub={`${bytes(m.disk_used)} / ${bytes(m.disk_total)}`}
              />
            )}
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                Load 1m/5m: {m.load1 != null ? m.load1.toFixed(2) : '—'} /{' '}
                {m.load5 != null ? m.load5.toFixed(2) : '—'}
              </span>
              <span>Containers: {m.containers_running ?? '—'}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{'\u2193'} {bytes(m.net_rx_bps ?? 0)}/s</span>
              <span>{'\u2191'} {bytes(m.net_tx_bps ?? 0)}/s</span>
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">No metrics yet.</p>
        )}

        {n.gpus && n.gpus.length > 0 && (
          <div className="border-t pt-2 space-y-1">
            <div className="text-xs text-muted-foreground">GPU</div>
            {n.gpus.map((g) => (
              <GpuRow key={g.gpu_uuid} gpu={g} />
            ))}
          </div>
        )}

        {Object.keys(n.labels || {}).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {Object.entries(n.labels).map(([k, v]) => (
              <Badge
                key={k}
                variant="outline"
                className="text-[10px] font-normal"
              >
                {k}={v}
              </Badge>
            ))}
          </div>
        )}

        <div className="pt-1 text-xs text-muted-foreground">
          {n.docker_id.slice(0, 12)} · first seen {relative(n.first_seen)}
        </div>
      </CardContent>
    </Card>
    </Link>
  )
}

export function NodesPage() {
  const { data, isLoading, error } = useNodes()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Nodes</h1>
        <p className="text-muted-foreground">Swarm members discovered by Zeus.</p>
      </div>

      {error && (
        <p className="text-sm text-destructive">Failed to load nodes.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}

        {data?.map((n) => (
          <NodeCard key={n.id} n={n} />
        ))}
      </div>
    </div>
  )
}
